import "./Sessions.css";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Selector } from "../../components/common/Selector";
import { fetchSessions, Session } from "../../api/sessions";
import { useGlobal } from "../../context/GlobalContext";
import { SessionsCollapsed } from "../../components/sessions/SessionsCollapsed";
import { SessionsExpanded } from "../../components/sessions/SessionsExpanded";

export function Sessions() {
    const { selectedProjectKey, userKey, isLoading: contextLoading } = useGlobal();
    const [searchParams, setSearchParams] = useSearchParams();
    const sessionId = searchParams.get('id');
    const [selectedTime, setSelectedTime] = useState('24h');
    const [sessions, setSessions] = useState<Session[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState('');
    const [hasMore, setHasMore] = useState(true);

    const timeOptions = [
        { key: '1h', label: '1 hour' },
        { key: '8h', label: '8 hours' },
        { key: '24h', label: '24 hours' },
        { key: '7d', label: '7 days' },
        { key: '15d', label: '15 days' },
        { key: '30d', label: '30 days' }
    ];

    useEffect(() => {
        const loadSessions = async () => {
            if (!selectedProjectKey || !userKey || contextLoading) return;

            setLoading(true);
            setError('');
            setSessions([]);
            setHasMore(true);

            try {
                const data = await fetchSessions(userKey, selectedProjectKey, selectedTime, 20, 0);
                setSessions(data);
                setHasMore(data.length === 20);
            } catch (err) {
                console.error('Failed to load sessions:', err);
                setError('Failed to load sessions');
            } finally {
                setLoading(false);
            }
        };

        loadSessions();
    }, [selectedProjectKey, userKey, selectedTime, contextLoading]);

    const handleLoadMore = async () => {
        if (!selectedProjectKey || !userKey) return;

        setLoadingMore(true);
        try {
            const data = await fetchSessions(userKey, selectedProjectKey, selectedTime, 10, sessions.length);
            setSessions(prev => [...prev, ...data]);
            setHasMore(data.length === 10);
        } catch (err) {
            console.error('Failed to load more sessions:', err);
        } finally {
            setLoadingMore(false);
        }
    };

    const handleExpand = (sessionId: string) => {
        setSearchParams({ id: sessionId });
    };

    const selectedSession = sessions.find(s => s.sessionId === sessionId);

    return (
        <div className="sessions-page">
            {selectedSession ? (
                <SessionsExpanded session={selectedSession} />
            ) : (
                <>
                    <div className="sessions-header">
                        <h1 className="sessions-title">Sessions</h1>
                        <div className="time-selector">
                            <Selector
                                options={timeOptions}
                                selectedKey={selectedTime}
                                onSelect={setSelectedTime}
                            />
                        </div>
                    </div>

                    <SessionsCollapsed 
                        sessions={sessions}
                        loading={loading}
                        error={error}
                        onExpand={handleExpand}
                        onLoadMore={handleLoadMore}
                        hasMore={hasMore}
                        loadingMore={loadingMore}
                    />
                </>
            )}
        </div>
    );
}
